import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Waves, Anchor, Wifi, Car, UtensilsCrossed, Sparkles, MapPin, Phone, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const LakeviewManor = () => {
  const navigate = useNavigate();
  const [bookingData, setBookingData] = useState({
    checkIn: '',
    checkOut: '',
    guests: '2'
  });

  const handleBooking = (e) => {
    e.preventDefault();
    alert('This is a demo site. Booking functionality is not active.');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="fixed top-6 left-6 z-50 bg-blue-800 text-white p-3 rounded-full shadow-lg hover:bg-blue-900 transition-all duration-300 hover:scale-110"
      >
        <ArrowLeft className="h-6 w-6" />
      </button>

      {/* Hero */}
      <header className="relative h-screen">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1920&q=80"
            alt="Lakeside Hotel"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/70 via-blue-900/40 to-transparent"></div>
        </div>

        <div className="relative h-full flex flex-col">
          <nav className="container mx-auto px-6 py-8 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Waves className="h-10 w-10 text-white" />
              <span className="text-2xl font-light text-white">Lakeview Manor</span>
            </div>
            <div className="hidden md:flex items-center gap-8 text-white/90 text-sm">
              <button onClick={() => document.getElementById('rooms').scrollIntoView({ behavior: 'smooth' })} className="hover:text-blue-300">Rooms</button>
              <button onClick={() => document.getElementById('dining').scrollIntoView({ behavior: 'smooth' })} className="hover:text-blue-300">Dining</button>
              <button onClick={() => document.getElementById('booking').scrollIntoView({ behavior: 'smooth' })} className="hover:text-blue-300">Book</button>
            </div>
          </nav>

          <div className="flex-1 flex items-center px-6">
            <div className="container mx-auto">
              <div className="max-w-2xl">
                <p className="text-blue-300 tracking-widest uppercase text-sm mb-6">Lake Geneva • Switzerland</p>
                <h1 className="text-6xl md:text-7xl font-light text-white mb-6 leading-tight">
                  Serenity on the Water
                </h1>
                <p className="text-xl text-white/80 mb-10">
                  Where timeless elegance meets breathtaking lakefront views. Experience Swiss luxury at its finest.
                </p>
                <Button 
                  onClick={() => document.getElementById('booking').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-white text-blue-900 hover:bg-blue-50 px-10 py-7 text-lg rounded-none"
                >
                  Reserve Your Stay
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Rooms Section */}
      <section id="rooms" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-blue-700 tracking-widest uppercase text-sm mb-4">Accommodations</p>
            <h2 className="text-5xl font-light text-slate-800">Lakeside Luxury</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {[
              { name: "Lake View Suite", price: "CHF 520", image: "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=600&q=80", desc: "Panoramic lake views, private balcony, king bed" },
              { name: "Garden Room", price: "CHF 380", image: "https://images.unsplash.com/photo-1590490360182-c33d57733427?w=600&q=80", desc: "Peaceful garden setting, queen bed, terrace access" },
              { name: "Penthouse Suite", price: "CHF 890", image: "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=600&q=80", desc: "Rooftop terrace, 180° views, private jacuzzi" },
              { name: "Classic Room", price: "CHF 290", image: "https://images.unsplash.com/photo-1591088398332-8a7791972843?w=600&q=80", desc: "Elegant comfort, courtyard view, queen bed" }
            ].map((room, i) => (
              <div key={i} className="group flex gap-6">
                <div className="w-1/2 h-64 overflow-hidden">
                  <img src={room.image} alt={room.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="w-1/2 flex flex-col justify-center">
                  <h3 className="text-2xl font-light text-slate-800 mb-2">{room.name}</h3>
                  <p className="text-slate-500 mb-4">{room.desc}</p>
                  <p className="text-blue-700 text-xl">From {room.price}<span className="text-slate-500 text-sm"> / night</span></p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Amenities */}
      <section className="py-24 bg-blue-900 text-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-6 gap-8 text-center">
            {[
              { icon: Waves, name: "Private Beach" },
              { icon: Anchor, name: "Boat Dock" },
              { icon: UtensilsCrossed, name: "Fine Dining" },
              { icon: Sparkles, name: "Spa & Wellness" },
              { icon: Wifi, name: "Free WiFi" },
              { icon: Car, name: "Valet Parking" }
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center">
                <item.icon className="h-10 w-10 text-blue-300 mb-4" />
                <p className="text-sm">{item.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dining Section */}
      <section id="dining" className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-blue-700 tracking-widest uppercase text-sm mb-4">Culinary Excellence</p>
              <h2 className="text-5xl font-light text-slate-800 mb-6">The Lakeside Restaurant</h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Our award-winning restaurant offers an exquisite dining experience with fresh, locally-sourced ingredients 
                and stunning views of Lake Geneva. Join us for breakfast, lunch, or an unforgettable dinner.
              </p>
              <p className="text-slate-500 italic">Open daily: 7:00 - 22:00</p>
            </div>
            <div className="h-96 overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80" 
                alt="Restaurant" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section id="booking" className="py-24 bg-slate-800 text-white">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="text-center mb-12">
            <p className="text-blue-400 tracking-widest uppercase text-sm mb-4">Reservations</p>
            <h2 className="text-5xl font-light">Book Your Stay</h2>
          </div>

          <form onSubmit={handleBooking} className="space-y-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <Label className="text-slate-300 mb-2 block">Check-in</Label>
                <Input type="date" value={bookingData.checkIn} onChange={(e) => setBookingData({...bookingData, checkIn: e.target.value})} className="bg-slate-700 border-slate-600 text-white h-12" required />
              </div>
              <div>
                <Label className="text-slate-300 mb-2 block">Check-out</Label>
                <Input type="date" value={bookingData.checkOut} onChange={(e) => setBookingData({...bookingData, checkOut: e.target.value})} className="bg-slate-700 border-slate-600 text-white h-12" required />
              </div>
              <div>
                <Label className="text-slate-300 mb-2 block">Guests</Label>
                <select value={bookingData.guests} onChange={(e) => setBookingData({...bookingData, guests: e.target.value})} className="w-full h-12 px-3 bg-slate-700 border border-slate-600 text-white rounded-md">
                  {[1,2,3,4].map(n => <option key={n} value={n}>{n} Guest{n > 1 ? 's' : ''}</option>)}
                </select>
              </div>
            </div>
            <Button type="submit" className="w-full bg-blue-700 hover:bg-blue-600 text-white py-6 text-lg">
              Check Availability
            </Button>
            <p className="text-center text-slate-400 text-xs italic">* Demo site - booking not functional</p>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 text-center text-slate-400 mb-8">
            <div><MapPin className="h-6 w-6 mx-auto mb-2" /><p>Montreux, Switzerland</p></div>
            <div><Phone className="h-6 w-6 mx-auto mb-2" /><p>+41 21 966 0000</p></div>
            <div><Mail className="h-6 w-6 mx-auto mb-2" /><p>reservations@lakeviewmanor.ch</p></div>
          </div>
          <p className="text-center text-slate-500 text-sm">© 2026 Lakeview Manor • Demo Site</p>
        </div>
      </footer>
    </div>
  );
};

export default LakeviewManor;
